/**
 * 📝 IntakeWizard — Wave 2 · Public Resident Intake
 * ════════════════════════════════════════════════════════════════════════
 *
 * 4-step public wizard at /intake. Field schema is fetched dynamically
 * from /api/v1/public/intake-schema so the SA can add/rename/delete
 * fields from the admin Intake Schema editor without a redeploy.
 *
 * Save-progress: draft is persisted to localStorage under
 * `tea_intake_draft_v1` so an applicant can pick up where they left
 * off, until they submit (which clears the draft).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Home } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentMeta from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

const DRAFT_KEY = "tea_intake_draft_v1";

export default function IntakeWizard() {
  useDocumentMeta({
    title:       "Request Housing Support | The Exchange Alliance",
    description: "Start your intake with The Exchange Alliance. A short, confidential form to request placement, case management, and workforce support.",
    canonical:   "https://theexchangealliance.com/intake",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [schema, setSchema]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [stepIdx, setStepIdx]   = useState(0);
  const [answers, setAnswers]   = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [errors, setErrors] = useState({});

  // Load schema + any saved draft on mount
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/v1/public/intake-schema`, { withCredentials: false })
      .then((r) => { if (!cancelled) setSchema(r.data); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load the intake form. Please refresh."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.answers) setAnswers(parsed.answers);
      }
    } catch { /* ignore malformed drafts */ }
    return () => { cancelled = true; };
  }, []);

  // Persist draft whenever answers change (best-effort)
  useEffect(() => {
    if (submitted) return;
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify({ answers, at: Date.now() }));
    } catch { /* localStorage disabled — silent fail */ }
  }, [answers, submitted]);

  const step = schema?.steps?.[stepIdx];
  const totalSteps = schema?.steps?.length || 0;

  const setField = (fieldId, value) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) setErrors((prev) => { const n = { ...prev }; delete n[fieldId]; return n; });
  };

  const validateStep = () => {
    if (!step) return true;
    const stepErrors = {};
    for (const f of step.fields || []) {
      if (!f.required) continue;
      const v = answers[f.id];
      if (v === undefined || v === null) { stepErrors[f.id] = "Required."; continue; }
      if (typeof v === "string" && !v.trim()) { stepErrors[f.id] = "Required."; continue; }
      if (typeof v === "boolean" && !v) { stepErrors[f.id] = "Required."; continue; }
      if (f.type === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v))) {
        stepErrors[f.id] = "Please enter a valid email.";
      }
    }
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const next = () => {
    if (!validateStep()) { toast.error("Please complete the required fields."); return; }
    setStepIdx((i) => Math.min(i + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStepIdx((i) => Math.max(0, i - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!validateStep()) { toast.error("Please complete the required fields."); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/v1/public/intake-submissions`, { data: answers }, { withCredentials: false });
      setSubmitted(r.data);
      try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      toast.success("Intake received.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed. Please review and try again.");
    } finally { setSubmitting(false); }
  };

  const clearDraft = () => {
    if (!window.confirm("Start over? Your current answers will be cleared.")) return;
    setAnswers({});
    setStepIdx(0);
    setErrors({});
    try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    toast.success("Draft cleared.");
  };

  const progressPct = totalSteps > 0 ? Math.round(((stepIdx + 1) / totalSteps) * 100) : 0;

  return (
    <main data-testid="intake-wizard-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans min-h-screen">
      <Header />

      <section className="pt-28 pb-8 md:pt-36 md:pb-10 border-b border-[#0A0612]/5" data-testid="intake-hero">
        <div className="mx-auto max-w-[900px] px-6 md:px-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1B4332]/40 bg-emerald-50 text-[#1B4332] font-mono text-[10px] tracking-[0.22em] uppercase mb-6">
            <Home className="h-3 w-3" />
            Housing Support · Intake
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-4">
            Ask for a seat at the table.
          </h1>
          <p className="text-lg text-[#0A0612]/70 max-w-2xl leading-relaxed">
            A short, confidential intake. Takes about 4 minutes. Your answers are only visible to the Alliance intake team — never shared publicly.
          </p>
        </div>
      </section>

      {loading && (
        <section className="py-16 text-center text-[#0A0612]/60" data-testid="intake-loading">
          Loading form…
        </section>
      )}

      {submitted && (
        <section className="py-16 md:py-24" data-testid="intake-thanks">
          <div className="mx-auto max-w-[700px] px-6 md:px-12 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/10 text-emerald-600 grid place-items-center mb-6">
              <Check className="h-8 w-8" />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">
              We received your intake.
            </h2>
            <p className="text-[#0A0612]/70 leading-relaxed mb-6">
              A confirmation just landed in your inbox. Our intake team will review your submission and reach out within one business day.
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 border border-[#0A0612] px-6 py-3 font-mono text-[11px] tracking-[0.18em] uppercase hover:bg-[#0A0612] hover:text-[#F4D88A] transition-colors"
              data-testid="intake-return-home"
            >
              Back to Home
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </section>
      )}

      {schema && !loading && !submitted && (
        <section className="py-10 md:py-14" data-testid="intake-form-section">
          <div className="mx-auto max-w-[900px] px-6 md:px-12">
            {/* Progress bar */}
            <div className="mb-8" data-testid="intake-progress">
              <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-[#0A0612]/60 mb-3">
                <span>Step {stepIdx + 1} of {totalSteps} · {step?.label}</span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-1.5 bg-[#0A0612]/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#1B4332] transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              {/* Step dots */}
              <div className="mt-4 flex gap-2 flex-wrap">
                {schema.steps.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      if (i <= stepIdx || Object.keys(answers).length > 0) setStepIdx(i);
                    }}
                    data-testid={`intake-step-dot-${s.id}`}
                    className={`text-xs px-3 py-1 rounded-full transition-colors font-mono tracking-[0.1em] uppercase ${
                      i === stepIdx
                        ? "bg-[#0A0612] text-[#F4D88A]"
                        : i < stepIdx
                        ? "bg-emerald-500/10 text-emerald-700 border border-emerald-500/30"
                        : "bg-white border border-[#0A0612]/15 text-[#0A0612]/50"
                    }`}
                  >
                    {i < stepIdx && <Check className="h-3 w-3 inline mr-1" />}
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Step body */}
            <div className="rounded-3xl bg-white border border-[#0A0612]/10 p-6 md:p-10 shadow-sm" data-testid={`intake-step-${step?.id}`}>
              <div className="mb-8">
                <div className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-1">
                  {step?.label}
                </div>
                {step?.description && (
                  <div className="text-[#0A0612]/60 text-sm md:text-base leading-relaxed">
                    {step.description}
                  </div>
                )}
              </div>

              <div className="space-y-5">
                {(step?.fields || []).map((f) => (
                  <DynamicField
                    key={f.id}
                    field={f}
                    value={answers[f.id]}
                    onChange={(v) => setField(f.id, v)}
                    error={errors[f.id]}
                  />
                ))}
              </div>

              <div className="mt-10 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  {stepIdx > 0 && (
                    <button
                      type="button"
                      onClick={back}
                      data-testid="intake-back-btn"
                      className="inline-flex items-center gap-2 border border-[#0A0612]/20 px-5 py-2.5 font-mono text-[10px] tracking-[0.22em] uppercase hover:bg-[#0A0612]/5 transition-colors"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={clearDraft}
                    data-testid="intake-clear-btn"
                    className="text-xs text-[#0A0612]/40 hover:text-red-600 underline"
                  >
                    Clear draft
                  </button>
                </div>
                <div>
                  {stepIdx < totalSteps - 1 ? (
                    <button
                      type="button"
                      onClick={next}
                      data-testid="intake-next-btn"
                      className="inline-flex items-center gap-2 bg-[#0A0612] text-[#F4D88A] px-6 py-3 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-[#1B4332] transition-colors"
                    >
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={submit}
                      disabled={submitting}
                      data-testid="intake-submit-btn"
                      className="inline-flex items-center gap-2 bg-[#1B4332] text-[#F4D88A] px-8 py-3 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-emerald-800 transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Submitting…" : "Submit Intake"}
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <p className="mt-6 text-xs text-[#0A0612]/40 leading-relaxed text-center max-w-xl mx-auto">
              Your progress is auto-saved to this browser. Zero information is shared publicly. If you need to talk to a human right away, email <a href="mailto:heather.a@theexchangealliance.com" className="underline">heather.a@theexchangealliance.com</a>.
            </p>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}


// ────────────────────────────────────────────────────────────────────
// Dynamic field renderer — mirrors the SA-editable schema
// ────────────────────────────────────────────────────────────────────
function DynamicField({ field, value, onChange, error }) {
  const testid = `intake-field-${field.id}`;

  const labelBlock = (
    <label className="block font-mono text-[9px] tracking-[0.25em] uppercase text-[#0A0612]/60 mb-2">
      {field.label}{field.required && <span className="text-red-600 ml-1">*</span>}
    </label>
  );

  const errorBlock = error && (
    <div className="mt-1.5 text-xs text-red-600" data-testid={`${testid}-error`}>
      {error}
    </div>
  );

  const baseInput = "w-full rounded-none border bg-white px-4 py-2.5 text-sm focus:outline-none";
  const inputCls = error
    ? `${baseInput} border-red-400 focus:border-red-600`
    : `${baseInput} border-[#0A0612]/15 focus:border-[#0A0612]`;

  if (field.type === "textarea") {
    return (
      <div>
        {labelBlock}
        <textarea
          rows={4}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          maxLength={field.maxlength || undefined}
          className={inputCls}
        />
        {errorBlock}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        {labelBlock}
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={inputCls}
        >
          <option value="">— Select —</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        {errorBlock}
      </div>
    );
  }

  if (field.type === "radio") {
    return (
      <div>
        {labelBlock}
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => onChange(o)}
              data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 border rounded-full text-sm transition-colors ${
                value === o
                  ? "bg-[#0A0612] text-[#F4D88A] border-[#0A0612]"
                  : "bg-white border-[#0A0612]/15 hover:border-[#0A0612]/60"
              }`}
            >
              {o}
            </button>
          ))}
        </div>
        {errorBlock}
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div>
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            data-testid={testid}
            className="mt-1 h-4 w-4 accent-[#1B4332]"
          />
          <span className="text-sm text-[#0A0612]/80 leading-relaxed">
            {field.label}
            {field.required && <span className="text-red-600 ml-1">*</span>}
          </span>
        </label>
        {errorBlock}
      </div>
    );
  }

  if (field.type === "number") {
    return (
      <div>
        {labelBlock}
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          data-testid={testid}
          min={field.min}
          max={field.max}
          className={inputCls}
        />
        {errorBlock}
      </div>
    );
  }

  return (
    <div>
      {labelBlock}
      <input
        type={field.type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={inputCls}
      />
      {errorBlock}
    </div>
  );
}
